/**
 * Sidebar Component
 *
 * Main navigation sidebar with Foundry branding.
 */

import { useMemo } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { colors, getRoleColor } from '@/theme';
import { useAuthStore } from '@/stores/auth';
import { useTasksStore } from '@/stores/tasks';
import { useSubscriptionStore } from '@/stores/subscription';
import {
  Home,
  MessageCircle,
  Eye,
  Users,
  Building2,
  Store,
  Coins,
  LogOut,
} from 'lucide-react';
import { CreditsBadge } from '@/components/CreditsBadge';

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { tasks } = useTasksStore();
  const { isFreeTier, stage } = useSubscriptionStore();

  // Map stage to tier name (aligned with Pricing page)
  const tierNames = ['IDEA', 'VALIDATE', 'BUILD', 'GROW', 'SCALE'] as const;
  const currentTier = tierNames[stage] || 'IDEA';

  // Derive badge counts from real store data
  const pendingApprovals = useMemo(
    () => tasks.filter(t => t.status === 'needs_approval').length,
    [tasks],
  );

  const navItems = useMemo(() => {
    const items = [
      { to: '/', icon: Home, label: 'Command Center', id: 'command-center' },
      { to: '/exchange', icon: MessageCircle, label: 'Exchange', id: 'exchange' },
      { to: '/watchtower', icon: Eye, label: 'Watchtower', id: 'watchtower', badge: pendingApprovals || undefined },
      { to: '/workforce', icon: Users, label: 'Workforce', id: 'workforce' },
      { to: '/marketplace', icon: Store, label: 'Marketplace', id: 'marketplace' },
      { to: '/core', icon: Building2, label: 'Company Core', id: 'core' },
    ];

    // Only show Credit History for paid accounts (stage 1+)
    if (!isFreeTier()) {
      items.push({ to: '/credit-history', icon: Coins, label: 'Credits', id: 'credit-history' });
    }

    return items;
  }, [pendingApprovals, isFreeTier]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <aside
      className="w-72 h-screen p-6 flex flex-col"
      style={{ background: colors.ironCharcoal, borderRight: `1px solid ${colors.forgeSteel}` }}
    >
      {/* Logo - Click to return to Command Center */}
      <Link to="/" className="flex items-center gap-3 mb-10 cursor-pointer transition-all hover:opacity-90">
        <div
          className="rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
          style={{
            width: '52px',
            height: '52px',
            background: `linear-gradient(135deg, ${colors.foundryCopper}, ${colors.emberOrange})`,
          }}
        >
          <img
            src="/aerware-icon-light.png"
            alt="Aerware"
            style={{ transform: 'scale(1.4)' }}
          />
        </div>
        <div className="flex flex-col items-center">
          <span
            style={{
              color: colors.sandstone,
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300,
              fontSize: '1.35rem',
              letterSpacing: '0.12em',
              lineHeight: 1.2,
            }}
          >
            AerwareAI
          </span>
          <span
            style={{
              color: colors.foundryCopper,
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '1.5rem',
              letterSpacing: '0.06em',
              lineHeight: 1.2,
            }}
          >
            FOUNDRY
          </span>
        </div>
      </Link>

      {/* Navigation */}
      <nav className="flex flex-col gap-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
                isActive ? 'font-medium' : ''
              )
            }
            style={({ isActive }) => ({
              background: isActive ? colors.ironCharcoal : 'transparent',
              color: isActive ? colors.sandstone : colors.secondaryText,
              borderLeft: isActive ? `3px solid ${colors.foundryCopper}` : '3px solid transparent',
            })}
          >
            <item.icon size={20} strokeWidth={1.5} />
            <span style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{item.label}</span>
            {item.badge && (
              <span
                className="ml-auto px-2 py-0.5 text-xs rounded-full"
                style={{ background: colors.foundryCopper, color: colors.obsidian }}
              >
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Spacer */}
      <div className="mt-auto" />

      {/* User Profile - Click to open Settings */}
      <div
        onClick={() => navigate('/settings')}
        className="mt-8 p-4 rounded-xl cursor-pointer transition-all duration-200 hover:translate-y-[-2px]"
        style={{ background: colors.obsidian, border: `1px solid ${colors.forgeSteel}` }}
      >
        <div className="flex items-center gap-3">
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="w-10 h-10 rounded-full object-cover"
              style={{ border: `2px solid ${getRoleColor(user.role)}` }}
            />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
              style={{
                background: getRoleColor(user?.role || 'admin'),
                color: colors.obsidian,
              }}
            >
              {getInitials(user?.name || 'U')}
            </div>
          )}
          <div>
            <p className="font-medium" style={{ color: colors.sandstone }}>
              {user?.name || 'User'}
            </p>
            <p className="text-xs" style={{ color: colors.electricTeal }}>
              Account Type: {currentTier}
            </p>
          </div>
        </div>
      </div>

      {/* Credits */}
      <div className="mt-3">
        <CreditsBadge />
      </div>

      {/* Logout Button */}
      <button
        onClick={() => {
          logout();
          navigate('/login');
        }}
        className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all duration-200 hover:scale-[1.02]"
        style={{
          background: colors.obsidian,
          border: `1px solid ${colors.forgeSteel}`,
          color: colors.secondaryText,
        }}
      >
        <LogOut size={18} strokeWidth={1.5} />
        <span className="text-sm font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Log Out
        </span>
      </button>
    </aside>
  );
}
